/**
 * Lo que se muestra al pasar el mouse: en la cascada, por un span; en
 * rendimiento, por una ruta.
 *
 * Existe por dos cosas que la cascada hacía mal y que solo se notan cuando ya
 * estás buscando un problema:
 *
 * 1. **El nombre se corta con puntos suspensivos y no tenía tooltip.** El
 *    `title` estaba en la barra de la línea de tiempo, no en el texto, así que
 *    pasar el mouse por el nombre truncado —que es lo natural— no mostraba
 *    nada.
 * 2. **En un span con error el tooltip mostraba el mensaje _en lugar_ del
 *    nombre.** Justo cuando más falta hace saber qué llamada falló.
 *
 * Y agrega lo que no estaba en ninguna pantalla: `url.full`, que trae el host
 * y el **puerto**. El nombre del span es solo `MÉTODO /ruta`; que una llamada
 * salga al 9443 y no al 443 no se veía por ninguna parte, y en un fallo de
 * CORS eso es la diferencia entre encontrarlo y no.
 *
 * Lo que **no** puede mostrar, y conviene tenerlo claro: la ruta con los
 * identificadores reales. El SDK la plantilla en el navegador antes de
 * enviarla —`/customers/{rut}/…`— y el query string se descarta. Acá no hay
 * nada escondido que se pueda revelar: no llegó nunca.
 */

export interface SpanParaDetalle {
  SpanName: string;
  StatusMessage?: string | undefined;
  SpanAttributes?: Record<string, string> | undefined;
}

export function detalleDeSpan(span: SpanParaDetalle): string {
  const lineas = [span.SpanName];

  // El nombre ya trae método y ruta; lo que agrega la URL es dónde vive el
  // servicio. Si el nombre ya la contiene, repetirla solo ocupa espacio.
  const url = span.SpanAttributes?.['url.full'];
  if (url && !span.SpanName.includes(url)) lineas.push(url);

  // El mensaje va **además** del nombre, nunca en su lugar.
  const mensaje = span.StatusMessage?.trim();
  if (mensaje) lineas.push(mensaje);

  return lineas.join('\n');
}

/**
 * Lo mismo para una fila de la tabla de rendimiento.
 *
 * Ahí la celda no se corta —la ruta se ve entera— así que el tooltip no está
 * para revelar texto escondido sino para agregar lo que la tabla no tiene: a
 * qué origen salen esas llamadas.
 *
 * Cuando hay más de uno se muestran todos. Es información y no ruido: una
 * misma ruta atendida desde dos puertos es exactamente el tipo de cosa que uno
 * quiere ver antes de perder una tarde.
 */
export function detalleDeRuta(ruta: string, urls: readonly string[] | undefined): string {
  const origenes = (urls ?? []).filter((u) => u && !ruta.includes(u));
  return [ruta, ...origenes].join('\n');
}
